import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, qs } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useActiveOrg } from '@/stores/auth';
import { inboxKey } from '@/features/inbox/useInboxCount';
import { Button } from '@/features/common/ui';

/**
 * Drafts of the catalogue, on the Customers and Items pages.
 *
 * ── WHY THE PAGES SHOW THEM AT ALL ──
 * The Inbox is where a draft is decided, and the owner said so: "only drafts
 * in inbox and I'll later confirm it". But a person on the Customers page
 * looking for PKPP Tech, which the agent proposed an hour ago, would find
 * nothing and add it again - a second row, then a duplicate to merge. So
 * the page has a "Drafts" chip with the count, the draft rows under it, and
 * the same Confirm and Discard the Inbox has, going to the SAME endpoints:
 * one place decides, reached from two screens. Everything else on the page
 * - the search, the default list, every picker - is active rows only.
 */

export type DraftKind = 'customer' | 'item';
export type CatalogueFilter = 'active' | 'draft';

/**
 * A picker's rows: never a draft, whatever came back. The server's default
 * list is active rows only, and every picker asks for `status=active` by
 * name; this is the belt to those braces - a cache written by an older
 * build, or a list that grows a status tomorrow, still never puts a name
 * nobody confirmed on an invoice.
 */
export function activeOnly<T extends object>(page: { items: T[] }): { items: T[] } {
  const isDraft = (row: object) => {
    const r = row as { status?: unknown; isDraft?: unknown };
    return r.status === 'draft' || r.isDraft === true;
  };
  return { ...page, items: page.items.filter((row) => !isDraft(row)) };
}

/** How many drafts wait, for the chip's badge. Under the list's own key, so a confirm anywhere refreshes it. */
export function useDraftCount(resource: 'customers' | 'items'): number {
  const org = useActiveOrg();
  const query = useQuery({
    queryKey: [resource, org?.id, 'drafts-count'],
    queryFn: () => api.get<{ items: unknown[] }>(`/${resource}${qs({ status: 'draft', limit: 200 })}`),
    enabled: !!org?.id,
  });
  return query.data?.items.length ?? 0;
}

/**
 * "All · Drafts 2": two chips, the pressed one filled. `aria-pressed` says
 * which to a screen reader, because the fill is a colour cue. The Drafts
 * chip is drawn even at zero, so the count going to zero after a confirm is
 * something a person sees happen rather than a chip that vanishes.
 */
export function DraftsFilter({
  value,
  count,
  onChange,
  noun,
}: {
  value: CatalogueFilter;
  count: number;
  onChange: (value: CatalogueFilter) => void;
  /** "customers" / "items" - for the group's name. */
  noun: string;
}) {
  const chip = (filter: CatalogueFilter, label: string, badge?: number) => {
    const pressed = value === filter;
    return (
      <button
        type="button"
        aria-pressed={pressed}
        onClick={() => onChange(filter)}
        data-testid={filter === 'draft' ? 'drafts-chip' : 'all-chip'}
        className={cn(
          'inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition-colors touch:min-h-11',
          pressed ? 'border-primary bg-primary text-primary-foreground' : 'border-input text-foreground hover:bg-accent active:bg-accent',
        )}
      >
        {label}
        {badge !== undefined ? (
          <span
            className={cn(
              'inline-flex min-w-5 justify-center rounded-full px-1.5 text-xs',
              pressed ? 'bg-primary-foreground/20' : 'bg-warning/15 text-warning',
            )}
            data-testid="drafts-count"
          >
            {badge}
          </span>
        ) : null}
      </button>
    );
  };
  return (
    <div role="group" aria-label={`Which ${noun}`} className="flex flex-wrap gap-2" data-testid="drafts-filter">
      {chip('active', 'All')}
      {chip('draft', 'Drafts', count)}
    </div>
  );
}

interface Outcome {
  confirmed: Array<{ type: string; id: string }>;
  failed: Array<{ type: string; id: string; reason: string }>;
}

/**
 * Confirm or discard ONE draft, through the Inbox's own endpoints - the
 * body is the Inbox's `{ items: [{ type, id }] }`, so there is one rule for
 * what confirming a draft does. The server's sentence, when it refuses, is
 * shown beside the row it refused, as the Inbox shows it.
 */
export function useDecideDraft(kind: DraftKind, resource: 'customers' | 'items') {
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const [reasons, setReasons] = useState<Map<string, string>>(() => new Map());
  const decide = useMutation({
    mutationFn: async ({ id, path }: { id: string; path: 'confirm' | 'discard' }) => {
      const outcome = await api.post<Outcome>(`/inbox/${path}`, { items: [{ type: kind, id }] });
      return { id, outcome };
    },
    onSuccess: ({ id, outcome }) => {
      setReasons((current) => {
        const next = new Map(current);
        const failed = outcome.failed.find((f) => f.id === id);
        if (failed) next.set(id, failed.reason);
        else next.delete(id);
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: [resource, org?.id] });
      void queryClient.invalidateQueries({ queryKey: inboxKey(org?.id) });
    },
    onError: (error, { id }) => {
      setReasons((current) => new Map(current).set(id, error instanceof Error ? error.message : 'Something went wrong'));
    },
  });
  return {
    confirm: (id: string) => decide.mutate({ id, path: 'confirm' }),
    discard: (id: string) => decide.mutate({ id, path: 'discard' }),
    busy: (id: string) => decide.isPending && decide.variables?.id === id,
    reason: (id: string) => reasons.get(id),
  };
}

/**
 * The line under a draft row, and its two buttons. The line says where
 * else the decision can be made, because the Inbox is where the batch it
 * came with - the deposit it is meant to be attached to - is waiting.
 */
export function DraftRowActions({
  id,
  name,
  decide,
  note,
  compact,
}: {
  id: string;
  name: string;
  decide: ReturnType<typeof useDecideDraft>;
  /** The agent's note on this row, when it left one. */
  note?: string | null;
  /** On a phone the buttons go under the line, full width. */
  compact?: boolean;
}) {
  const reason = decide.reason(id);
  return (
    <div className={cn('mt-1.5 flex gap-2', compact ? 'flex-col' : 'flex-wrap items-center')} data-testid="draft-row-actions">
      <p className="text-xs text-muted-foreground">
        Proposed by your AI agent - confirm it in the{' '}
        <Link to="/inbox" className="underline" data-tap-exempt="inline link in a sentence">
          Inbox
        </Link>{' '}
        or here.
        {note ? <span className="italic"> {note}</span> : null}
      </p>
      <div className="flex shrink-0 items-center gap-1.5">
        <Button size="sm" loading={decide.busy(id)} onClick={() => decide.confirm(id)} aria-label={`Confirm ${name}`}>
          Confirm
        </Button>
        <Button size="sm" variant="outline" disabled={decide.busy(id)} onClick={() => decide.discard(id)} aria-label={`Discard ${name}`}>
          Discard
        </Button>
      </div>
      {reason ? (
        <p role="alert" className="basis-full text-xs text-destructive">
          {reason}
        </p>
      ) : null}
    </div>
  );
}
